import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog } from "@/components/ui/dialog";
import { useCreateIdentify } from "@/hooks/use-identifies";
import { useEndpoints } from "@/hooks/use-endpoints";
import { toast } from "sonner";
import { HelpIcon } from "@/components/ui/help-icon";

interface CreateIdentifyModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}

export function CreateIdentifyModal({ open, onClose, onCreated }: CreateIdentifyModalProps) {
  const createMut = useCreateIdentify();
  const { data: endpoints } = useEndpoints();
  const [name, setName] = useState("");
  const [endpointId, setEndpointId] = useState<string | null>(null);
  const [match, setMatch] = useState("");
  const [srvLookups, setSrvLookups] = useState(true);

  const handleSave = () => {
    if (!name.trim() || !match.trim()) return;
    createMut.mutate(
      {
        name: name.trim(),
        endpoint_id: endpointId,
        match: match.trim(),
        match_header: null,
        match_request_uri: null,
        srv_lookups: srvLookups,
      },
      {
        onSuccess: (data) => {
          toast.success("Identify created");
          onCreated(data.id);
          setName("");
          setEndpointId(null);
          setMatch("");
          setSrvLookups(true);
          onClose();
        },
        onError: (e) => toast.error(`Failed: ${(e as Error).message}`),
      }
    );
  };

  return (
    <Dialog open={open} onClose={onClose} title="Create Identify">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Name * <HelpIcon text="Identify rule name. Example: match-trunk-a." /></Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="identify-1"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Endpoint <HelpIcon text="Which endpoint to use when this Identify rule matches. If None, the rule is inactive." /></Label>
            <Select
              value={endpointId ?? ""}
              onChange={(e) => setEndpointId(e.target.value || null)}
            >
              <option value="">None</option>
              {endpoints?.map((ep) => (
                <option key={ep.id} value={ep.id}>{ep.name}</option>
              ))}
            </Select>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Match IP/CIDR * <HelpIcon text="Source IP or CIDR range. Asterisk matches first rule whose match covers the source IP. Example: 192.168.1.0/24." /></Label>
          <Input
            value={match}
            onChange={(e) => setMatch(e.target.value)}
            placeholder="192.168.1.0/24"
          />
        </div>

        <Switch
          id="srv_lookups"
          checked={srvLookups}
          onChange={(e) => setSrvLookups(e.target.checked)}
          label="SRV Lookups"
        />

        <div className="flex justify-end gap-2 pt-4 border-t border-border">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={createMut.isPending || !name.trim() || !match.trim()}>
            Create
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
